import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { StorageManager } from './storage-manager';

export interface UploadProgress {
  progress: number;
  state: 'idle' | 'uploading' | 'success' | 'error';
}

export const uploadImage = async (
  file: File,
  userId: string,
  type: 'avatar' | 'banner' | 'signature',
  onProgress?: (progress: UploadProgress) => void
): Promise<string> => {
  try {
    console.log('Starting upload:', { 
      fileName: file.name, 
      fileType: file.type,
      fileSize: file.size,
      userId,
      type
    });

    onProgress?.({ progress: 0, state: 'uploading' });

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      const error = new Error('Invalid file type. Please upload a JPG, PNG, GIF, or WebP file.');
      console.error('File type validation failed:', error);
      onProgress?.({ progress: 0, state: 'error' });
      throw error;
    }

    // Validate file size (25MB limit)
    const MAX_SIZE = 25 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      const error = new Error('File size too large. Maximum size is 25MB.');
      console.error('File size validation failed:', error);
      onProgress?.({ progress: 0, state: 'error' });
      throw error;
    }

    // Use organized storage manager
    const { publicUrl } = await StorageManager.uploadWithOrganization({
      file,
      userId,
      bucket: 'profile-images',
      type,
      maxSize: MAX_SIZE
    });

    onProgress?.({ progress: 100, state: 'success' });
    console.log('Generated public URL:', publicUrl);
    
    // Ensure we have a valid URL string
    if (!publicUrl || typeof publicUrl !== 'string') {
      console.error('Invalid URL returned from storage manager:', publicUrl);
      throw new Error('Failed to generate a valid image URL');
    }
    
    // For banner images, ensure URL is properly formatted
    if (type === 'banner') {
      console.log('Validating banner URL format:', publicUrl);
      // If URL doesn't start with http but contains storage path components,
      // it might be a relative URL that needs the base URL prepended
      if (!publicUrl.startsWith('http') && publicUrl.includes('/object/')) {
        console.warn('Banner URL appears to be in relative format, may need base URL');
      }
    }
    
    return publicUrl;

  } catch (error) {
    console.error('Error in uploadImage:', error);
    toast({
      title: "Upload Failed",
      description: error.message || "Failed to upload image. Please try again.",
      variant: "destructive",
    });
    onProgress?.({ progress: 0, state: 'error' });
    throw error;
  }
};